import { jsPDF } from "jspdf";

const SCORE_ROWS = [
  ["demand", "Demand"],
  ["competition", "Competition"],
  ["breakout", "Breakout"],
  ["trend", "Trend"],
  ["idea_capacity", "Idea capacity"],
  ["shorts_fit", "Shorts fit"],
  ["longform_fit", "Long-form fit"],
  ["monetization", "Monetization"],
  ["difficulty", "Ease of production"],
  ["copyright_risk", "Copyright safety"],
];

const CYAN = [0, 178, 209];
const GREEN = [34, 197, 94];
const AMBER = [217, 155, 0];
const ORANGE = [200, 110, 40];
const RED = [220, 60, 60];
const GRAY = [110, 118, 130];
const DARK = [30, 33, 40];

function scoreRGB(v) {
  if (v == null) return GRAY;
  if (v >= 75) return GREEN;
  if (v >= 55) return AMBER;
  if (v >= 35) return ORANGE;
  return RED;
}

export function downloadNichesPdf(niches, meta = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 48;
  const contentW = pageW - M * 2;
  let y = M;

  const ensure = (need) => {
    if (y + need > pageH - M) {
      doc.addPage();
      y = M;
    }
  };

  const text = (str, { size = 10, style = "normal", color = DARK, gap = 4, indent = 0 } = {}) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(String(str), contentW - indent);
    const lh = size * 1.35;
    for (const line of lines) {
      ensure(lh);
      doc.text(line, M + indent, y);
      y += lh;
    }
    y += gap;
  };

  const heading = (str) => {
    ensure(22);
    doc.setDrawColor(0, 178, 209);
    doc.setLineWidth(2);
    doc.line(M, y - 2, M + 22, y - 2);
    text(str.toUpperCase(), { size: 9, style: "bold", color: CYAN, gap: 6 });
  };

  const rule = () => {
    ensure(14);
    doc.setDrawColor(220, 222, 226);
    doc.setLineWidth(0.6);
    doc.line(M, y, pageW - M, y);
    y += 14;
  };

  // ---- Header ----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(DARK[0], DARK[1], DARK[2]);
  doc.text("YTFactory Niche Research", M, y);
  y += 22;

  const sub = meta.topic ? `Topic: ${meta.topic}` : "Recommended niches for faceless English channels";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
  doc.text(sub, M, y);
  y += 14;
  doc.setFontSize(8.5);
  doc.text(
    `Generated ${new Date().toLocaleString()} · ${niches.length} niche${niches.length === 1 ? "" : "s"} · Ranked by Opportunity Score`,
    M, y,
  );
  y += 10;
  rule();

  niches.forEach((n, idx) => {
    ensure(70);
    // Rank + score + name
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    const sc = scoreRGB(n.opportunity_score);
    doc.setTextColor(sc[0], sc[1], sc[2]);
    doc.text(`${n.opportunity_score ?? "—"}`, M, y);
    doc.setTextColor(DARK[0], DARK[1], DARK[2]);
    const nameLines = doc.splitTextToSize(`${idx + 1}. ${n.name || "Untitled niche"}`, contentW - 42);
    doc.text(nameLines, M + 40, y);
    y += Math.max(18, nameLines.length * 16) + 2;

    const metaBits = [];
    if (n.trend_direction) metaBits.push(`Trend: ${n.trend_direction}`);
    metaBits.push(`Opportunity Score: ${n.opportunity_score ?? "—"}/100`);
    text(metaBits.join("   ·   "), { size: 8.5, color: GRAY, gap: 6 });

    if (n.demand_note) text(n.demand_note, { size: 9, style: "italic", color: GRAY });
    if (n.why_recommended) text(n.why_recommended, { size: 9.5 });

    // Scores
    heading("Scores");
    const scores = n.scores || {};
    const colW = contentW / 2;
    const startY = y;
    let col = 0;
    let rowY = y;
    SCORE_ROWS.forEach(([key, label], i) => {
      const val = scores[key];
      const c = scoreRGB(val);
      const x = M + col * colW;
      ensure(0);
      if (rowY + 13 > pageH - M) {
        doc.addPage();
        y = M; rowY = M;
      }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
      doc.text(label, x, rowY);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(c[0], c[1], c[2]);
      doc.text(`${val ?? "—"}`, x + colW - 30, rowY);
      if (col === 1) rowY += 14;
      col = col === 0 ? 1 : 0;
    });
    y = rowY + (col === 1 ? 14 : 0) + 6;

    // Formats
    if (n.faceless_style) { heading("Faceless style"); text(n.faceless_style, { size: 9.5 }); }
    if (n.shorts_format) { heading("Shorts format"); text(n.shorts_format, { size: 9.5 }); }
    if (n.longform_format) { heading("Long-form format"); text(n.longform_format, { size: 9.5 }); }

    if (Array.isArray(n.series) && n.series.length) {
      heading(`${n.series.length} repeatable series`);
      n.series.forEach((s) => text(`• ${s}`, { size: 9.5, gap: 1, indent: 8 }));
      y += 4;
    }
    if (Array.isArray(n.sample_titles) && n.sample_titles.length) {
      heading("Sample titles");
      n.sample_titles.forEach((t) => text(`• ${t}`, { size: 9.5, gap: 1, indent: 8 }));
      y += 4;
    }
    if (Array.isArray(n.video_ideas) && n.video_ideas.length) {
      heading(`${n.video_ideas.length} starter video ideas`);
      n.video_ideas.forEach((v, i) => text(`${i + 1}. ${v}`, { size: 9.5, gap: 1, indent: 8 }));
      y += 4;
    }
    if (Array.isArray(n.example_channels) && n.example_channels.length) {
      heading("Successful example channels");
      n.example_channels.forEach((c) => {
        text(`• ${c.name || ""}`, { size: 9.5, style: "bold", gap: 1, indent: 8 });
        if (c.why) text(c.why, { size: 9, color: GRAY, gap: 3, indent: 18 });
      });
    }

    if (idx < niches.length - 1) rule();
  });

  // Footer page numbers
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(GRAY[0], GRAY[1], GRAY[2]);
    doc.text("YTFactory · Niche Research", M, pageH - 24);
    doc.text(`Page ${i} of ${pages}`, pageW - M, pageH - 24, { align: "right" });
  }

  const slug = (meta.topic || "niches")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 40) || "niches";
  doc.save(`ytfactory-niche-research-${slug}.pdf`);
}
